import React from "react";
import { Button } from "../../../";
import { ReportStatusSelectors } from "../constants";
import { Controller, useFormContext } from "react-hook-form";
import { useReportControls } from "../hooks/ReportControlContext";
import { useReportMessage } from "../hooks/ReportMessageContext";
import { useReportSendOptions } from "../hooks/ReportPublishContext";
import { DateInput, FormInput, FormSelect, NumberInput } from "../../../forms";
import { GenericUserSelect, ReportAuthorSelect, ReportTypesSelect } from "../../../forms/selects";
import { ClientReportStatusRecipients } from "../types";
import { DefReactSelectOption } from "../../../../types/DefReactSelectOption";
import { PatchClientReportForm } from "../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";

/**
 * This is the main body of the edit report modal that contains the majority of the editable fields
 */
export const FormBody = ({ isLoading: isProcessing } : Readonly<{ isLoading: boolean }>) => {
    const statusControlRef = React.useRef(null);
    const { control, formState, setValue, watch } = useFormContext<PatchClientReportForm>();
    const authorId = watch('authorId');
    const status = watch('status');

    const { openSendOptions } = useReportSendOptions();
    const { clearMessage, createMessage } = useReportMessage();
    const { 
        currentStatus,
        isAllowedToAssignAuthor, 
        isAllowedToSetActualMinutes, 
        isAllowedToSetDeadlineDate, 
        isAllowedToSetReportType, 
        isLoading: isLoadingStatuses,
        isReportEditable,
        report,
        reportStatuses,
        reportThreadId
    } = useReportControls();
    
    const onOpenSendOptions = () => openSendOptions();

    const onStatusChanged = (newStatusData: DefReactSelectOption<ClientReportStatusRecipients>) => {
        const newStatus = reportStatuses.find(el => el.value === newStatusData.value);
        const originalStatus = reportStatuses.find(el => el.value === report?.statusId);
        
        const updateStatus = (statusRecord: DefReactSelectOption<ClientReportStatusRecipients>) => {
            // Handles all other status changes that are NOT the same as the current report status
            setValue('statusId', newStatusData.value, { shouldDirty: true });
            setValue('status', newStatusData.data!.selector, { shouldDirty: true });

            if (statusRecord.data!.selector !== report?.status || authorId !== report?.authorId) {
                createMessage(reportThreadId!, statusRecord.data!);
            }
            else {
                clearMessage();
            }
        }

        // reset if the same status that the report is currently on is re-selected
        if (!newStatus || newStatus.data!.selector === report?.status) {
            setValue('statusId', newStatusData.value, { shouldDirty: true });
            setValue('status', newStatusData.data!.selector, { shouldDirty: true });
            clearMessage();
            return;
        }
        else if (newStatus.data!.selector === ReportStatusSelectors.SentToClient) {
            // Handles when the report is being marked as "sent to client"
            openSendOptions().then(
                () => {
                    // this will update the status of the patch report
                    updateStatus(newStatus);
                },
                () => {
                    // @ts-ignore
                    statusControlRef.current.setValue(originalStatus);
                }
            );
        }
        else {
            updateStatus(newStatus);
        }
    };
    
    const isLoading = isLoadingStatuses || isProcessing;
    const isFormLocked = isLoading || report?.isStatusCompliant === true || report?.isStatusTerminated === true;
    return (
        <div className="row gy-2">
            <div className="col-12">
                <Controller
                    name="reportName"
                    control={control}
                    rules={{ required: "Report Name is required." }}
                    render={({ field: { ref, onChange, ...rest } }) => (
                        <FormInput 
                            label="Report Name" 
                            disableAnimations={true}
                            disabled={isFormLocked} 
                            onChange={(e) => setValue('reportName', e.target.value, { shouldDirty: true })}
                            {...rest} 
                        />
                    )}
                />
            </div>
            <div className="col-12">
                <Controller
                    name="typeId"
                    control={control}
                    rules={{ required: "A Report Type must be selected." }}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <ReportTypesSelect
                            label="Report Type"
                            defaultValue={value}
                            disableAnimations={true}
                            isDisabled={!isAllowedToSetReportType || isFormLocked}
                            onChange={(values) => setValue('typeId', values.value, { shouldDirty: true })}
                            {...rest}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="authorId"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <ReportAuthorSelect
                            label="Author"
                            defaultValue={value}
                            disableAnimations={true}
                            isDisabled={!isAllowedToAssignAuthor || isFormLocked}
                            placeholder="No Author assigned."
                            onChange={(values) => {
                                setValue('authorId', values.value, { shouldDirty: true });

                                let newStatus : ClientReportStatusRecipients | null;
                                if (!currentStatus?.isTerminalStatus && !currentStatus?.isComplianceStatus) {
                                    // Set to Author Assigned if the status is neither terminal nor compliance
                                    newStatus = reportStatuses.find(el => el.data!.selector === ReportStatusSelectors.AuthorAssigned)?.data ?? null;
                                    if (newStatus) {
                                        setValue('statusId', newStatus.id, { shouldDirty: true });
                                        setValue('status', newStatus.selector, { shouldDirty: true });
                                    }
                                } else {
                                    newStatus = currentStatus;
                                }

                                createMessage(reportThreadId!, newStatus!, values.value);
                            }}
                            {...rest}
                        />
                    )}
                />
            </div>
            <div className="col-6 col-md-3">
                <Controller
                    name="actualMinutesToComplete"
                    control={control}
                    render={({ field: { ref, onChange, ...rest } }) => (
                        <NumberInput 
                            label="Minutes to Complete" 
                            disableAnimations={true} 
                            decimalScale={0} 
                            errorMessage={formState.errors?.actualMinutesToComplete?.message}
                            errorAllowRetry={false}
                            disabled={!isAllowedToSetActualMinutes || isFormLocked}
                            onChange={(e, value) => setValue('actualMinutesToComplete', value.floatValue, { shouldDirty: true })}
                            {...rest} />
                    )}
                />
            </div>
            <div className="col-12 col-md-3">
                <Controller
                    name="deadlineDate"
                    control={control}
                    render={({ field: { ref, onBlur, onChange, ...rest } }) => (
                        <DateInput
                            label="Deadline"
                            placeholder="Choose Report deadline."
                            disableAnimations={true}
                            isClearable
                            disabled={!isAllowedToSetDeadlineDate || isFormLocked}
                            onBlur={(value, e) => {
                                if (value !== "Invalid date") {
                                    setValue('deadlineDate', value, { shouldDirty: true });
                                }
                            }}
                            {...rest}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex flex-row align-items-end">
                <div className="flex-fill">
                    <Controller
                        name="statusId"
                        control={control}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <FormSelect
                                ref={statusControlRef}
                                label="Status"
                                defaultValue={value}
                                disableAnimations={true}
                                isDisabled={isLoading || report?.isStatusTerminated || !report?.authorId}
                                isLoadingOptions={isLoading}
                                options={reportStatuses}
                                onChange={(rec : DefReactSelectOption<ClientReportStatusRecipients>) => onStatusChanged(rec)}
                                {...rest}
                            />
                        )}
                    />
                </div>
                {status === ReportStatusSelectors.SentToClient && (
                    <div className="ms-1">
                        <Button disabled={report?.isStatusTerminated} onClick={() => onOpenSendOptions()}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </div>
                )}
            </div>
            <div className="col-6">
                <Controller
                    name="complianceUserId"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <GenericUserSelect
                            label="Compliance User"
                            defaultValue={value}
                            disableAnimations={true}
                            isDisabled={!isAllowedToAssignAuthor || isFormLocked}
                            placeholder="No Compliance user assigned."
                            onChange={(values) => setValue('complianceUserId', values.value, { shouldDirty: true })}
                            {...rest}
                        />
                    )}
                />
            </div>
        </div>
    );
};